import React, { useState, useEffect, useMemo } from 'react';
import { Menu, X, Settings, Users, BookOpen, TrendingUp, FileText, Home } from 'lucide-react';
import { useAccessibility } from './hooks/useAccessibility';
import { useStudents } from './hooks/useStudents';
import { useGoals } from './hooks/useGoals';
import { seedSampleData } from './utils/localStorage';

import Dashboard from './components/Dashboard';
import StudentList from './components/StudentList';
import StudentDetail from './components/StudentDetail';
import GoalEditor from './components/GoalEditor';
import ProgressTracker from './components/ProgressTracker';
import Reports from './components/Reports';
import SettingsPanel from './components/SettingsPanel';
import ScreenReaderContent from './components/ScreenReaderContent';
import Footer from './components/Footer';
import InstallPrompt from './components/InstallPrompt';
// Logo is in public folder, will reference directly

function App() {
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedGoalId, setSelectedGoalId] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const { settings, updateAccessibilitySetting, announce } = useAccessibility();
  const { students, loading: studentsLoading } = useStudents();
  const { goals, loading: goalsLoading } = useGoals();

  const navigation = useMemo(() => [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'students', label: 'Students', icon: Users },
    { id: 'goals', label: 'Goals', icon: BookOpen },
    { id: 'progress', label: 'Progress', icon: TrendingUp },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings }
  ], []);

  useEffect(() => {
    seedSampleData();
    
    // Handle URL-based navigation for PWA shortcuts
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view');
    if (viewParam && navigation.find(item => item.id === viewParam)) {
      setCurrentView(viewParam);
    }
  }, [navigation]);

  const handleNavigation = (viewId, options = {}) => {
    setCurrentView(viewId);
    setSelectedStudentId(options.studentId || null);
    setSelectedGoalId(options.goalId || null);
    setIsMobileMenuOpen(false);
    
    const navItem = navigation.find(item => item.id === viewId);
    if (navItem) {
      announce(`Navigated to ${navItem.label}`);
    }
  };

  const renderCurrentView = () => {
    const commonProps = {
      students,
      goals,
      onNavigate: handleNavigation,
      announce
    };

    switch (currentView) {
      case 'dashboard':
        return <Dashboard {...commonProps} />;
      case 'students':
        return selectedStudentId ? (
          <StudentDetail 
            {...commonProps} 
            studentId={selectedStudentId}
            onBack={() => {
              setSelectedStudentId(null);
              announce('Returned to student list');
            }}
          />
        ) : (
          <StudentList {...commonProps} />
        );
      case 'goals':
        return <GoalEditor {...commonProps} goalId={selectedGoalId} />;
      case 'progress':
        return <ProgressTracker {...commonProps} />;
      case 'reports':
        return <Reports {...commonProps} />;
      case 'settings':
        return (
          <SettingsPanel 
            settings={settings} 
            onSettingChange={updateAccessibilitySetting}
            announce={announce}
          />
        );
      default:
        return <Dashboard {...commonProps} />;
    }
  };

  if (studentsLoading || goalsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading IEP Management Tool...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Skip to main content link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-white focus:px-4 focus:py-2 focus:rounded"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className="bg-gray-900 shadow-sm border-b header-bg print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="hamburger-menu-button p-2 rounded-md text-gray-300 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary header-button"
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <ScreenReaderContent>
                  {isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                </ScreenReaderContent>
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              
              <div className="flex items-center ml-2">
                <img 
                  src="/logo.svg" 
                  alt="IEP Management Tool Logo" 
                  className="h-8 w-auto mr-3 logo-adaptive"
                />
                <h1 className="text-xl font-bold text-white header-title">
                  IEP Mgmt
                </h1>
              </div>
            </div>
            
            <nav className="main-navigation space-x-4" role="navigation" aria-label="Main navigation">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.id)}
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors header-nav-button ${
                      currentView === item.id
                        ? 'bg-primary text-white'
                        : 'text-gray-300 hover:text-white hover:bg-gray-700'
                    }`}
                    aria-current={currentView === item.id ? 'page' : undefined}
                  >
                    <Icon size={16} className="mr-2" />
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="mobile-menu" id="mobile-menu">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t mobile-menu-bg bg-gray-900">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.id)}
                    className={`flex items-center w-full px-3 py-2 rounded-md text-base font-medium transition-colors mobile-nav-button ${
                      currentView === item.id
                        ? 'bg-primary text-white'
                        : 'text-gray-300 hover:text-white hover:bg-gray-700'
                    }`}
                    aria-current={currentView === item.id ? 'page' : undefined}
                  >
                    <Icon size={20} className="mr-3" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main id="main-content" className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" tabIndex="-1">
        {renderCurrentView()}
      </main>

      {/* Footer */}
      <Footer />

      {/* PWA Install Prompt */}
      <InstallPrompt announce={announce} />

      {/* Live region for screen reader announcements */}
      <div id="aria-live-region" aria-live="polite" aria-atomic="false" className="sr-only"></div>
    </div>
  );
}

export default App;